import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, from } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { UserService } from 'app/core/services/auth/user.service';
import { PermissionService } from 'app/core/permission/permission.service';
import { ApiService } from 'app/shared/api.service';
import { environment } from 'environments/environment';
import { StorageService } from './storage.service';
import { Router } from '@angular/router';

@Injectable()
export class AuthService {
    private _authenticated: boolean = false;

    constructor(
        private _httpClient: HttpClient,
        private _userService: UserService,
        private _permissionService: PermissionService,
        private api: ApiService,
        private key: StorageService,
        private router: Router,
    ) {}

    get accessToken(): string {
        const encryptedToken = localStorage.getItem('a_id');
        if (!encryptedToken) {
            return '';
        }
        // 1. Coba dekripsi AES
        try {
            const decrypted = this.key.decryptionAES(encryptedToken);
            if (decrypted && decrypted.trim().length > 0) {
                try {
                    return JSON.parse(decrypted);
                } catch {
                    return decrypted.replace(/^"+|"+$/g, '');
                }
            }
        } catch {}
        // 2. Fallback: jika tersimpan sebagai plain JWT atau dev token
        try {
            return JSON.parse(encryptedToken);
        } catch {
            return encryptedToken.replace(/^"+|"+$/g, '');
        }
    }

    /**
     * Process & save user data from API response into storage and services
     */
    private _processAndSaveUserData(userFromApi: any): any {
        if (!userFromApi) {
            return null;
        }

        // Extract permissions
        const permissions: string[] = [];
        if (Array.isArray(userFromApi.permissions)) {
            permissions.push(...userFromApi.permissions);
        }
        if (
            userFromApi.role &&
            typeof userFromApi.role === 'object' &&
            Array.isArray(userFromApi.role.permissions)
        ) {
            permissions.push(...userFromApi.role.permissions);
        }
        if (Array.isArray(userFromApi.roles)) {
            for (const r of userFromApi.roles) {
                if (r && Array.isArray(r.permissions)) {
                    permissions.push(...r.permissions);
                }
            }
        }

        // Determine is_verified
        let isVerified = false;
        if (typeof userFromApi.is_verified === 'boolean') {
            isVerified = userFromApi.is_verified;
        } else if (userFromApi.verified_at) {
            isVerified = true;
        } else if (userFromApi.is_verified === 1 || userFromApi.is_verified === '1') {
            isVerified = true;
        }

        const userData = {
            ...userFromApi,
            id: userFromApi.id || '',
            name: userFromApi.name || 'User',
            username: userFromApi.username || userFromApi.email || '',
            email: userFromApi.email || '',
            role: userFromApi.role || null,
            roles:
                userFromApi.roles ||
                (userFromApi.role && typeof userFromApi.role === 'object'
                    ? [userFromApi.role]
                    : []),
            avatar: userFromApi.avatar || '',
            status: userFromApi.status ?? 'login',
            is_verified: isVerified,
            verified_at: userFromApi.verified_at || null,
            permissions: permissions,
        };

        const isDevSession = localStorage.getItem('dev_session') === 'true';
        if (isDevSession) {
            localStorage.setItem('user', JSON.stringify(userData));
        } else {
            localStorage.setItem('user', this.key.encryptData(userData));
        }

        this._userService.user = userData;
        this._permissionService.loadFromUser(userData);
        this._authenticated = true;

        return userData;
    }

    /**
     * Fetch user profile & permissions from auth/me API
     */
    async fetchUserProfile(): Promise<boolean> {
        try {
            const meResponse: any = await this.api.get(
                environment.me || 'auth/me',
                true,
            );

            if (meResponse && meResponse.success && meResponse.data) {
                this._processAndSaveUserData(meResponse.data);
                return true;
            } else if (
                meResponse &&
                meResponse.data &&
                (meResponse.data.id || meResponse.data.email || meResponse.data.name)
            ) {
                this._processAndSaveUserData(meResponse.data);
                return true;
            } else if (
                meResponse &&
                (meResponse.id || meResponse.email || meResponse.name)
            ) {
                this._processAndSaveUserData(meResponse);
                return true;
            }

            // Jika response menunjukkan token expired / unauthenticated
            if (
                meResponse?.message?.toLowerCase().includes('unauthenticated') ||
                meResponse?.message?.toLowerCase().includes('token expired')
            ) {
                this.signOut().subscribe();
                return false;
            }

            return this._authenticated;
        } catch (error: any) {
            console.error('[AuthService] Failed to fetch user profile from auth/me:', error);
            if (error?.response?.status === 401) {
                this.signOut().subscribe();
                return false;
            }
            return this._authenticated;
        }
    }

    /**
     * Public method to reload user profile and permissions on-demand
     */
    getProfile(): Observable<any> {
        return from(this.fetchUserProfile()).pipe(
            map(() => this.getCurrentUser()),
        );
    }

    /**
     * Sign in
     */
    async signIn(credentials: {
        username?: string;
        email?: string;
        password: string;
    }): Promise<boolean> {
        if (this._authenticated) {
            await this.signOut().toPromise();
        }

        const payload = {
            username: credentials.username || credentials.email,
            password: credentials.password,
        };

        try {
            const response: any = await this.api.post(
                environment.auth,
                payload,
                false,
            );

            if (response && response.success) {
                const token = response.data?.access_token;
                if (!token) {
                    throw new Error('Access token not found in response');
                }

                const expiresIn = response.data?.expires_in || 3600;
                const expiredAt = Date.now() + expiresIn * 1000;

                // Store encrypted token
                localStorage.setItem('a_exp', this.key.encryptData(expiredAt));
                localStorage.setItem('a_id', this.key.encryptData(token));

                let userFromApi = response.data?.user;
                if (!userFromApi) {
                    try {
                        const meResponse: any = await this.api.get(
                            environment.me || 'auth/me',
                            true,
                        );
                        if (
                            meResponse &&
                            meResponse.success &&
                            meResponse.data
                        ) {
                            userFromApi = meResponse.data;
                        } else if (meResponse && meResponse.data) {
                            userFromApi = meResponse.data;
                        }
                    } catch (meErr) {
                        console.warn(
                            '[AuthService] Failed to fetch auth/me profile:',
                            meErr,
                        );
                    }
                }

                if (!userFromApi) {
                    throw new Error(
                        'Failed to retrieve user profile from server',
                    );
                }

                this._processAndSaveUserData(userFromApi);

                // Start refresh token scheduler
                if (this.api.startTokenScheduler) {
                    this.api.startTokenScheduler(expiresIn);
                }

                return true;
            }

            return false;
        } catch (error: any) {
            console.error('Login failed:', error);

            let errorMessage = 'Login failed. Please check your credentials.';

            if (error?.message) {
                errorMessage = error.message;
            } else if (error?.response?.data?.message) {
                errorMessage = error.response.data.message;
            }

            throw new Error(errorMessage);
        }
    }

    /**
     * Get SSO authorization URL from backend API
     */
    async getSsoRedirectUrl(): Promise<string> {
        try {
            const response: any = await this.api.get('auth/sso/redirect-url', false);
            if (response && response.success && response.data?.url) {
                return response.data.url;
            }
            throw new Error(response?.message || 'Gagal mengambil URL otorisasi SSO.');
        } catch (error: any) {
            console.error('Failed to get SSO redirect URL:', error);
            throw new Error(error?.message || 'Gagal terhubung ke layanan SSO.');
        }
    }

    /**
     * Process SSO token received from OAuth callback
     */
    async handleSsoToken(token: string, expiresIn: number = 3600): Promise<boolean> {
        if (this._authenticated) {
            try {
                await this.signOut().toPromise();
            } catch (e) {
                // ignore signout errors
            }
        }

        const numericExpiresIn = typeof expiresIn === 'string' ? parseInt(expiresIn, 10) : expiresIn;
        const validExpiresIn = isNaN(numericExpiresIn) ? 3600 : numericExpiresIn;
        const expiredAt = Date.now() + validExpiresIn * 1000;

        // Store encrypted token
        localStorage.setItem('a_exp', this.key.encryptData(expiredAt));
        localStorage.setItem('a_id', this.key.encryptData(token));

        const userFetched = await this.fetchUserProfile();
        if (userFetched) {
            if (this.api.startTokenScheduler) {
                this.api.startTokenScheduler(validExpiresIn);
            }
            return true;
        }

        throw new Error('Gagal memuat profil pengguna setelah login SSO.');
    }

    /**
     * Sign in using token (auto login & restore session on reload)
     */
    signInUsingToken(): Observable<boolean> {
        const tokenEnc = localStorage.getItem('a_id');
        const userEnc = localStorage.getItem('user');
        const isDevSession = localStorage.getItem('dev_session') === 'true';

        if (!tokenEnc) {
            return of(false);
        }

        // 1. Initial preload from localStorage agar UI tidak flicker
        if (userEnc) {
            try {
                let user: any;

                if (isDevSession) {
                    user = JSON.parse(userEnc);
                } else {
                    user = JSON.parse(this.key.decryptionAES(userEnc));
                }

                if (user) {
                    if (!user.role) {
                        user.role = 'user';
                    }
                    this._userService.user = user;
                    this._permissionService.loadFromUser(user);
                    this._authenticated = true;
                }
            } catch (e) {
                console.warn('[AuthService] Token restore from storage failed:', e);
            }
        }

        // 2. Fetch fresh user profile & permissions from backend auth/me
        return from(this.fetchUserProfile()).pipe(
            map((success) => {
                return success;
            }),
            catchError((err) => {
                console.warn('[AuthService] auth/me fetch error on reload:', err);
                return of(this._authenticated);
            }),
        );
    }

    /**
     * Sign out - HIT API LOGOUT
     */
    signOut(): Observable<boolean> {
        return new Observable((observer) => {
            const isDevSession = localStorage.getItem('dev_session') === 'true';

            // Dev session: langsung clear tanpa hit API
            if (isDevSession) {
                this.clearAuthData();
                observer.next(true);
                observer.complete();
                return;
            }

            if (this.accessToken) {
                this.api
                    .post('auth/logout', {}, true)
                    .then((response: any) => {
                        console.log('Logout API Response:', response);
                        this.clearAuthData();
                        observer.next(true);
                        observer.complete();
                    })
                    .catch((error) => {
                        console.error('Logout API Error:', error);
                        this.clearAuthData();
                        observer.next(true);
                        observer.complete();
                    });
            } else {
                this.clearAuthData();
                observer.next(true);
                observer.complete();
            }
        });
    }

    /**
     * Clear authentication data
     */
    private clearAuthData(): void {
        localStorage.clear();
        this._authenticated = false;
        this._userService.user = null;
        this._permissionService.clear();
    }

    /**
     * Check authentication status
     */
    check(): Observable<boolean> {
        if (this._authenticated) {
            return of(true);
        }

        // Dev session bypass: tidak perlu token terenkripsi
        const isDevSession = localStorage.getItem('dev_session') === 'true';
        if (isDevSession && localStorage.getItem('a_id')) {
            return this.signInUsingToken();
        }

        if (!this.accessToken) {
            return of(false);
        }

        return this.signInUsingToken();
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        return this._authenticated;
    }

    /**
     * Check if current user is verified
     */
    isUserVerified(): boolean {
        const user = this.getCurrentUser();
        if (!user) {
            return false;
        }
        if (typeof user.is_verified === 'boolean') {
            return user.is_verified;
        }
        if (user.is_verified === 1 || user.is_verified === '1') {
            return true;
        }
        return !!user.verified_at;
    }

    /**
     * Check if current user has completed mandatory employee onboarding profile data
     */
    isProfileCompleted(): boolean {
        const user = this.getCurrentUser();
        if (!user) {
            return false;
        }

        // Check global persistent skip flag for testing/development
        if (localStorage.getItem('profile_completed_general') === 'true') {
            return true;
        }

        // If explicitly flagged
        if (typeof user.is_profile_completed === 'boolean') {
            return user.is_profile_completed;
        }

        // If user already has NIP / NIK and employment status filled
        if (user.nip || user.nik) {
            return true;
        }

        // Check local persistent flag for user ID
        if (user.id && localStorage.getItem(`profile_completed_${user.id}`) === 'true') {
            return true;
        }

        return false;
    }

    /**
     * Skip employee profile onboarding for now (temporary testing)
     */
    skipProfileCompletion(): void {
        const user = this.getCurrentUser();
        if (user && user.id) {
            localStorage.setItem(`profile_completed_${user.id}`, 'true');
        }
        localStorage.setItem('profile_completed_general', 'true');
    }

    /**
     * Save employee onboarding profile data via POST employee/profile
     */
    async saveEmployeeProfile(payload: FormData | any): Promise<{ success: boolean; message?: string; data?: any }> {
        const currentUser = this.getCurrentUser();
        if (!currentUser) {
            return { success: false, message: 'Pengguna tidak ditemukan dalam sesi.' };
        }

        try {
            const response: any = await this.api.postAuto('employee/profile', payload);

            if (response && response.success && response.data) {
                // Save updated user data from backend
                const updatedUser = this._processAndSaveUserData(response.data);

                if (currentUser.id) {
                    localStorage.setItem(`profile_completed_${currentUser.id}`, 'true');
                }
                localStorage.setItem('profile_completed_general', 'true');

                return {
                    success: true,
                    message: response.message || 'Data profil karyawan berhasil disimpan',
                    data: updatedUser,
                };
            } else {
                let errorMsg = response?.message;
                if (response?.errors && typeof response.errors === 'object') {
                    const firstErr = Object.values(response.errors).flat()[0];
                    if (firstErr) {
                        errorMsg = String(firstErr);
                    }
                }
                return {
                    success: false,
                    message: errorMsg || 'Gagal menyimpan profil karyawan.',
                };
            }
        } catch (error: any) {
            console.error('[AuthService] saveEmployeeProfile error:', error);
            const msg =
                error?.response?.data?.message ||
                error?.message ||
                'Terjadi kesalahan sistem saat menyimpan data.';
            return { success: false, message: msg };
        }
    }

    /**
     * Get current logged in user
     */
    getCurrentUser(): any {
        const userEnc = localStorage.getItem('user');
        if (!userEnc) {
            return null;
        }

        const isDevSession = localStorage.getItem('dev_session') === 'true';

        // 1. Jika mode dev session, parse langsung
        if (isDevSession) {
            try {
                return JSON.parse(userEnc);
            } catch (e) {
                console.warn('[AuthService] Failed to parse dev session user JSON:', e);
            }
        }

        // 2. Coba parse plain JSON terlebih dahulu (karena sering disimpan langsung)
        try {
            const parsed = JSON.parse(userEnc);
            if (parsed && typeof parsed === 'object') {
                return parsed;
            }
        } catch {
            // Bukan plain JSON, lanjut ke dekripsi AES
        }

        // 3. Coba dekripsi AES
        try {
            const decrypted = this.key.decryptionAES(userEnc);
            if (decrypted && decrypted.trim().length > 0) {
                const user = JSON.parse(decrypted);
                if (user && typeof user === 'object') {
                    return user;
                }
            }
        } catch (error) {
            console.warn('[AuthService] AES decryption failed for user data:', error);
        }

        return null;
    }

    /**
     * Get user name
     */
    getUserName(): string {
        const user = this.getCurrentUser();
        return user?.name || 'Guest';
    }

    /**
     * Get specific user property
     */
    getUserProperty(property: string): any {
        const user = this.getCurrentUser();
        return user ? user[property] : null;
    }

    /**
     * Check if user is logged in
     */
    isLoggedIn(): boolean {
        return !!this.getCurrentUser();
    }

    /**
     * Get user gates
     */
    getUserGates(): any[] {
        const user = this.getCurrentUser();
        return user?.gates || [];
    }

    /**
     * Check if user has specific gate
     */
    hasGate(gateId: string): boolean {
        const gates = this.getUserGates();
        return gates.some((gate) => gate.id === gateId);
    }
}
